import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Search,
  RefreshCw,
  X,
  Save,
  ChevronDown,
  AlertCircle,
  Check,
  Server as ServerIcon
} from 'lucide-react';
import type { User } from '../../types';

interface AwsServer {
  _id: string;
  institute: { _id: string; name: string };
  serverName: string;
  instanceType: string;
  region: string;
  os: string;
  status: 'active' | 'stopped' | 'terminated';
  addedBy?: { name?: string; email?: string };
  lastUpdatedBy?: { name?: string; email?: string };
  createdAt?: string;
}

interface Institute {
  _id: string;
  name: string;
  address?: string;
}

interface AwsServerInventoryProps {
  currentUser: User;
}

const INSTANCE_TYPE_SUGGESTIONS = [
  't2.micro', 't2.small', 't2.medium', 't2.large', 't2.xlarge',
  't3.micro', 't3.small', 't3.medium', 't3.large', 't3.xlarge',
  't3a.medium', 't3a.large',
  'm5.large', 'm5.xlarge', 'm6i.large',
  'c5.large', 'c5.xlarge'
];

const REGION_OPTIONS = [
  { value: 'ap-south-1', label: 'ap-south-1 — Mumbai' },
  { value: 'ap-south-2', label: 'ap-south-2 — Hyderabad' },
  { value: 'ap-southeast-1', label: 'ap-southeast-1 — Singapore' },
  { value: 'ap-southeast-2', label: 'ap-southeast-2 — Sydney' },
  { value: 'ap-northeast-1', label: 'ap-northeast-1 — Tokyo' },
  { value: 'us-east-1', label: 'us-east-1 — N. Virginia' },
  { value: 'us-east-2', label: 'us-east-2 — Ohio' },
  { value: 'us-west-1', label: 'us-west-1 — N. California' },
  { value: 'us-west-2', label: 'us-west-2 — Oregon' },
  { value: 'eu-west-1', label: 'eu-west-1 — Ireland' },
  { value: 'eu-central-1', label: 'eu-central-1 — Frankfurt' }
];

const OS_OPTIONS = [
  'Ubuntu 20.04', 'Ubuntu 22.04', 'Ubuntu 24.04',
  'Amazon Linux 2', 'Amazon Linux 2023',
  'Debian 11', 'Debian 12',
  'Red Hat Enterprise Linux 8', 'Red Hat Enterprise Linux 9',
  'CentOS 7',
  'Windows Server 2019', 'Windows Server 2022'
].map(os => ({ value: os, label: os }));

// Sentinel option value that flips the field into a free-text fallback —
// AWS adds regions/OS images faster than this list can be kept current.
const OTHER_SENTINEL = '__other__';

interface SelectWithOtherProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  otherPlaceholder: string;
  className: string;
}

const SelectWithOther: React.FC<SelectWithOtherProps> = ({ value, onChange, options, placeholder, otherPlaceholder, className }) => {
  const [isOther, setIsOther] = useState(() => value !== '' && !options.some(o => o.value === value));

  if (isOther) {
    return (
      <div className="flex gap-1 items-center">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={otherPlaceholder}
          autoFocus
          className={className}
        />
        <button
          type="button"
          onClick={() => { setIsOther(false); onChange(''); }}
          className="text-xs text-blue-600 hover:text-blue-700 underline shrink-0"
        >
          list
        </button>
      </div>
    );
  }

  return (
    <select
      value={value}
      onChange={(e) => {
        if (e.target.value === OTHER_SENTINEL) {
          setIsOther(true);
          onChange('');
        } else {
          onChange(e.target.value);
        }
      }}
      className={className}
    >
      <option value="">{placeholder}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      <option value={OTHER_SENTINEL}>Other…</option>
    </select>
  );
};

const emptyForm = {
  serverName: '',
  instanceType: '',
  region: '',
  os: '',
  status: 'active' as AwsServer['status']
};

const AwsServerInventory: React.FC<AwsServerInventoryProps> = ({ currentUser }) => {
  const [servers, setServers] = useState<AwsServer[]>([]);
  const [institutes, setInstitutes] = useState<Institute[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedInstitutes, setExpandedInstitutes] = useState<Set<string>>(new Set());

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<Partial<AwsServer> | null>(null);

  const [showNewForm, setShowNewForm] = useState(false);
  const [newInstituteId, setNewInstituteId] = useState('');
  const [newServer, setNewServer] = useState(emptyForm);

  const canEdit = ['devops', 'pm', 'admin'].includes(currentUser.role);

  useEffect(() => {
    fetchServers();
    fetchInstitutes();
  }, []);

  const fetchServers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch('/api/aws-servers');
      if (!response.ok) throw new Error('Failed to fetch AWS servers');
      const data = await response.json();
      setServers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch AWS servers');
    } finally {
      setLoading(false);
    }
  };

  const fetchInstitutes = async () => {
    try {
      const res = await fetch('/api/institutes');
      if (res.ok) {
        const data = await res.json();
        setInstitutes(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching institutes:', err);
    }
  };

  const getFilteredServers = () => {
    let filtered = [...servers];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(s => s.status === statusFilter);
    }

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(s =>
        s.serverName.toLowerCase().includes(searchLower) ||
        s.instanceType.toLowerCase().includes(searchLower) ||
        s.institute.name.toLowerCase().includes(searchLower) ||
        (s.region || '').toLowerCase().includes(searchLower) ||
        (s.os || '').toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  };

  const groupByInstitute = (list: AwsServer[]) => {
    const grouped: Record<string, AwsServer[]> = {};
    list.forEach(server => {
      const key = server.institute?.name || 'Unknown';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(server);
    });
    return grouped;
  };

  const toggleExpand = (instituteName: string) => {
    const next = new Set(expandedInstitutes);
    if (next.has(instituteName)) next.delete(instituteName);
    else next.add(instituteName);
    setExpandedInstitutes(next);
  };

  const handleSaveNew = async () => {
    const institute = institutes.find(i => i._id === newInstituteId);
    if (!institute) {
      setError('Please select an institute');
      return;
    }
    if (!newServer.serverName.trim() || !newServer.instanceType.trim()) {
      setError('Server name and instance type are required');
      return;
    }

    try {
      const response = await fetch('/api/aws-servers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          institute: { _id: institute._id, name: institute.name },
          ...newServer,
          addedBy: currentUser
        })
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Failed to save server');
      }

      const saved = await response.json();
      setServers([...servers, saved]);
      setSuccess('Server added successfully');
      setShowNewForm(false);
      setNewInstituteId('');
      setNewServer(emptyForm);
      setExpandedInstitutes(prev => new Set(prev).add(institute.name));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save server');
    }
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editingData) return;

    try {
      const response = await fetch(`/api/aws-servers/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...editingData,
          lastUpdatedBy: currentUser
        })
      });

      if (!response.ok) throw new Error('Failed to update server');

      const updated = await response.json();
      setServers(servers.map(s => s._id === editingId ? updated : s));
      setSuccess('Server updated successfully');
      setEditingId(null);
      setEditingData(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update server');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this server record?')) return;

    try {
      const response = await fetch(`/api/aws-servers/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete server');

      setServers(servers.filter(s => s._id !== id));
      setSuccess('Server record deleted');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete server');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-300',
      stopped: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-yellow-300',
      terminated: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-300'
    };
    return colors[status] || colors.active;
  };

  const filteredServers = getFilteredServers();
  const groupedServers = groupByInstitute(filteredServers);
  const colSpan = canEdit ? 7 : 6;

  const renderServerRow = (server: AwsServer) => (
    <tr key={server._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white pl-12">
        {editingId === server._id ? (
          <input
            type="text"
            value={editingData?.serverName ?? ''}
            onChange={(e) => setEditingData({ ...editingData, serverName: e.target.value })}
            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full"
          />
        ) : (
          server.serverName
        )}
      </td>
      <td className="px-6 py-4 text-sm">
        {editingId === server._id ? (
          <input
            type="text"
            list="instance-type-options"
            value={editingData?.instanceType ?? ''}
            onChange={(e) => setEditingData({ ...editingData, instanceType: e.target.value })}
            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-28"
          />
        ) : (
          <span className="font-mono text-blue-600 dark:text-blue-400">{server.instanceType}</span>
        )}
      </td>
      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
        {editingId === server._id ? (
          <SelectWithOther
            value={editingData?.region ?? ''}
            onChange={(v) => setEditingData({ ...editingData, region: v })}
            options={REGION_OPTIONS}
            placeholder="-- Region --"
            otherPlaceholder="Custom region"
            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-32 text-xs"
          />
        ) : (
          server.region || '—'
        )}
      </td>
      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
        {editingId === server._id ? (
          <SelectWithOther
            value={editingData?.os ?? ''}
            onChange={(v) => setEditingData({ ...editingData, os: v })}
            options={OS_OPTIONS}
            placeholder="-- OS --"
            otherPlaceholder="Custom OS"
            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-32 text-xs"
          />
        ) : (
          server.os || '—'
        )}
      </td>
      <td className="px-6 py-4 text-sm">
        {editingId === server._id ? (
          <select
            value={editingData?.status ?? 'active'}
            onChange={(e) => setEditingData({ ...editingData, status: e.target.value as AwsServer['status'] })}
            className={`px-2 py-1 border rounded text-xs font-medium ${getStatusColor(editingData?.status || 'active')}`}
          >
            <option value="active">Active</option>
            <option value="stopped">Stopped</option>
            <option value="terminated">Terminated</option>
          </select>
        ) : (
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(server.status)}`}>
            {server.status}
          </span>
        )}
      </td>
      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
        {server.createdAt ? new Date(server.createdAt).toLocaleDateString() : '—'}
      </td>
      {canEdit && (
        <td className="px-6 py-4 text-center">
          <div className="flex items-center justify-center gap-2">
            {editingId === server._id ? (
              <>
                <button
                  onClick={handleSaveEdit}
                  className="p-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded hover:bg-green-200"
                >
                  <Save className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setEditingId(null); setEditingData(null); }}
                  className="p-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => { setEditingId(server._id); setEditingData(server); }}
                  className="p-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded hover:bg-blue-200"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(server._id)}
                  className="p-1.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded hover:bg-red-200"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </td>
      )}
    </tr>
  );

  return (
    <div className="space-y-6 pb-8">
      <datalist id="instance-type-options">
        {INSTANCE_TYPE_SUGGESTIONS.map(t => <option key={t} value={t} />)}
      </datalist>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ServerIcon className="w-7 h-7" />
            AWS Server Inventory
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manually tracked AWS instances, grouped by institute</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchServers}
            disabled={loading}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {canEdit && (
            <button
              onClick={() => setShowNewForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Server
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-2">
          <Check className="w-5 h-5 text-green-600" />
          <p className="text-green-700 dark:text-green-400">{success}</p>
        </div>
      )}

      {/* New Server Form */}
      {showNewForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 space-y-4">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg font-bold">Add New Server</h2>
            <button onClick={() => setShowNewForm(false)} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Institute *</label>
              <select
                value={newInstituteId}
                onChange={(e) => setNewInstituteId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">-- Select an Institute --</option>
                {institutes.map(inst => (
                  <option key={inst._id} value={inst._id}>{inst.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Server Name *</label>
              <input
                type="text"
                value={newServer.serverName}
                onChange={(e) => setNewServer({ ...newServer, serverName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g., college-app-prod-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Instance Type *</label>
              <input
                type="text"
                list="instance-type-options"
                value={newServer.instanceType}
                onChange={(e) => setNewServer({ ...newServer, instanceType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g., t3.medium"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Region</label>
              <SelectWithOther
                value={newServer.region}
                onChange={(v) => setNewServer({ ...newServer, region: v })}
                options={REGION_OPTIONS}
                placeholder="-- Select Region --"
                otherPlaceholder="e.g., ap-south-1"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">OS</label>
              <SelectWithOther
                value={newServer.os}
                onChange={(v) => setNewServer({ ...newServer, os: v })}
                options={OS_OPTIONS}
                placeholder="-- Select OS --"
                otherPlaceholder="e.g., Ubuntu 22.04"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select
                value={newServer.status}
                onChange={(e) => setNewServer({ ...newServer, status: e.target.value as AwsServer['status'] })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="active">Active</option>
                <option value="stopped">Stopped</option>
                <option value="terminated">Terminated</option>
              </select>
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowNewForm(false)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveNew}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Save Server
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 space-y-4">
        <div className="flex gap-4 flex-wrap items-center">
          <div className="flex-1 min-w-xs relative">
            <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search servers, institutes, instance types..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="stopped">Stopped</option>
            <option value="terminated">Terminated</option>
          </select>
        </div>

        <div className="text-sm text-gray-600 dark:text-gray-400">
          Showing {filteredServers.length} of {servers.length} servers
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-8">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      )}

      {/* Table */}
      {!loading && filteredServers.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Server Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Instance Type</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Region</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">OS</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Added</th>
                  {canEdit && <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                {Object.entries(groupedServers).map(([instituteName, list]) => (
                  <React.Fragment key={instituteName}>
                    <tr
                      className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-150 dark:hover:bg-gray-650 cursor-pointer"
                      onClick={() => toggleExpand(instituteName)}
                    >
                      <td colSpan={colSpan} className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <ChevronDown
                            className={`w-5 h-5 text-gray-700 dark:text-gray-300 transition-transform ${expandedInstitutes.has(instituteName) ? 'rotate-180' : ''}`}
                          />
                          <span className="font-semibold text-gray-900 dark:text-white">{instituteName}</span>
                          <span className="ml-4 inline-block px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                            {list.length} server{list.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </td>
                    </tr>

                    {expandedInstitutes.has(instituteName) && list.map(server => renderServerRow(server))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredServers.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">No servers found</p>
          {canEdit && (
            <button
              onClick={() => setShowNewForm(true)}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Add first server
            </button>
          )}
        </div>
      )}

      {/* Permissions Info */}
      {!canEdit && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            View-only access. Contact PM, DevOps, or Admin to manage this inventory.
          </p>
        </div>
      )}
    </div>
  );
};

export default AwsServerInventory;
